// firebase.ts

import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, connectAuthEmulator } from 'firebase/auth';
import { getAnalytics } from 'firebase/analytics';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';

// ── Emulator config ───────────────────────────────────────
// REACT_APP_USE_EMULATORS = master switch (true → all local)
// REACT_APP_EMU_AUTH / _FIRESTORE / _STORAGE = per-service override
//   Set individual ones to 'false' to skip that emulator,
//   or to 'true' when the master switch is off.
// ──────────────────────────────────────────────────────────

const env = (key: string) => process.env[key];
const isProduction = env('REACT_APP_FIREBASE_MODE') !== 'development';
const getEnvSuffix = () => (isProduction ? 'PROD' : 'DEV');

// Per-service emulator: explicit value wins, otherwise falls back to master
const masterEmu = env('REACT_APP_USE_EMULATORS') === 'true';
const shouldEmu = (key: string) => {
  const v = env(key);
  return v !== undefined ? v === 'true' : masterEmu;
};
const emuAuth = shouldEmu('REACT_APP_EMU_AUTH');
const emuFirestore = shouldEmu('REACT_APP_EMU_FIRESTORE');
const emuStorage = shouldEmu('REACT_APP_EMU_STORAGE');

const firebaseConfig = {
  apiKey: env(`REACT_APP_APIKEY_${getEnvSuffix()}`),
  authDomain: env(`REACT_APP_AUTHDOMAIN_${getEnvSuffix()}`),
  projectId: env(`REACT_APP_PROJECT_ID_${getEnvSuffix()}`),
  storageBucket: env(`REACT_APP_STORAGE_BUCKET_${getEnvSuffix()}`),
  messagingSenderId: env(`REACT_APP_MESSAGING_SENDER_ID_${getEnvSuffix()}`),
  appId: env(`REACT_APP_APP_ID_${getEnvSuffix()}`),
  measurementId: env(`REACT_APP_MEASUREMENT_ID_${getEnvSuffix()}`),
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);
const mainAuth = getAuth(app);
const provider = new GoogleAuthProvider();
provider.setCustomParameters({ prompt: 'select_account' });

const db = getFirestore(app);
const storage = getStorage(app);

// ── Emulator connections ──────────────────────────────────
if (emuAuth || emuFirestore || emuStorage) {
  console.log('🔧 Firebase Emulators:');
  if (emuAuth) {
    connectAuthEmulator(mainAuth, 'http://localhost:9099', { disableWarnings: true });
    console.log('  ✅ Auth      → localhost:9099');
  }
  if (emuFirestore) {
    connectFirestoreEmulator(db, 'localhost', 8888);
    console.log('  ✅ Firestore → localhost:8888');
  }
  if (emuStorage) {
    connectStorageEmulator(storage, 'localhost', 9199);
    console.log('  ✅ Storage   → localhost:9199');
  }
}

// Dynamic auth getter that returns appropriate auth instance based on session storage
export const getAuthInstance = () => {
  try {
    // Check if we have impersonation token in session storage
    const impersonationToken = sessionStorage.getItem('impersonation.token');
    
    if (impersonationToken) {
      // Import and initialize impersonation auth if we have a token
      const { getImpersonationAuth } = require('./firebase/impersonationAuth');
      const impersonationAuth = getImpersonationAuth();
      
      if (impersonationAuth) {
        console.log('🔥 Using impersonation auth instance');
        return impersonationAuth;
      } else {
        console.log('⚠️ Impersonation token found but auth not initialized, falling back to main auth');
      }
    }
  } catch (error) {
    console.log('⚠️ Error checking impersonation session, falling back to main auth:', error);
  }
  
  console.log('🔥 Using main auth instance');
  return mainAuth;
};

// For backward compatibility, export the main auth as 'auth'
// But new code should use getAuthInstance() for dynamic auth selection
export const auth = getAuthInstance();

export { provider, app, db, storage };
